/**
 * Live Activity dashboard — snapshot API client.
 * GET /api/live-activity/snapshot/ (admin-only; bearer + X-Schema like the analytics hooks).
 */
import { getBackendURL } from "../components/env";

async function authHeaders() {
  const mod = await import("../components/supabaseClient");
  const { data } = await mod.supabasePublic().auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("No active session — sign in again");
  return { Authorization: `Bearer ${token}`, "X-Schema": mod.supabaseSchema || "public" };
}

export async function fetchLiveActivitySnapshot({ at = null, windowSec = 3600, onlineSec = 90, recentSec = 600 } = {}) {
  const params = new URLSearchParams({
    window: String(windowSec), online_secs: String(onlineSec), recent_secs: String(recentSec),
  });
  if (at) params.set("at", at);
  const res = await fetch(`${getBackendURL()}/api/live-activity/snapshot/?${params.toString()}`, {
    headers: await authHeaders(),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

async function postJson(path, body) {
  const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
  const res = await fetch(`${getBackendURL()}${path}`, { method: "POST", headers, body: JSON.stringify(body || {}) });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

/** The memory-issue catalogue for the Memory Issues tab (superadmin only). */
export function fetchMemoryIssues() {
  return postJson("/api/admin/memory-issues/status/", {});
}

/** Run one issue's check on the live backend and return its verdict. */
export function verifyMemoryIssue(issueId) {
  return postJson("/api/admin/memory-issues/verify/", { issue_id: issueId });
}

/**
 * Run the read-only DB health scan on the live backend and return its report (superadmin only).
 *
 * The backend asks Supabase who is calling, so when Supabase itself is down it can't
 * run the full scan for anyone. It then answers 503 with Supabase's own health under
 * `outage`, which is returned here as the report (with `outage_reason`) rather than
 * thrown: that outage is the finding. For the same reason a missing session doesn't
 * stop the request, since a browser can't renew its session while sign-in is down.
 *
 * `live` marks a refresh from a tab that is watching: the backend answers those from
 * the quick facts and repeats the slow parts only every few minutes. A failure carries
 * the HTTP `status` (409: a scan is already running).
 */
export async function runDbHealthCheck({ live = false } = {}) {
  let headers = { "Content-Type": "application/json" };
  let sessionError = null;
  try {
    headers = { ...headers, ...(await authHeaders()) };
  } catch (e) {
    sessionError = e;
  }
  const res = await fetch(`${getBackendURL()}/api/admin/db-health/scan/`, {
    method: "POST", headers, body: JSON.stringify(live ? { live: true } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (json.outage) return { ...json.outage, outage_reason: json.error };
  if (sessionError) throw sessionError;
  if (!res.ok || json.error) {
    const failure = new Error(json.error || `HTTP ${res.status}`);
    failure.status = res.status;
    throw failure;
  }
  return json;
}

export function runCodeReview() {
  return postJson("/api/admin/code-review/run/", {});
}

async function getJson(path) {
  const res = await fetch(`${getBackendURL()}${path}`, { headers: await authHeaders() });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

/** The nightly AI reviews of each day's commits, newest first (superadmin only). */
export function fetchNightlyCodeReviews() {
  return getJson("/api/admin/code-review/nightly/");
}

/** The nightly AI review's findings for one tab: area memory | database | general, status open | fixed | dismissed. */
export function fetchCodeFindings({ area, status = "open" }) {
  return getJson(`/api/admin/code-findings/?${new URLSearchParams({ area, status }).toString()}`);
}

/** Close an open AI finding that isn't a real problem, with the reason why. */
export function dismissCodeFinding(id, reason) {
  return postJson(`/api/admin/code-findings/${encodeURIComponent(id)}/dismiss/`, { reason });
}

/** Last night's run of one check: kind code_review | memory_scan | memory_probes | db_health. */
export function fetchLatestNightlyCheck(kind) {
  return getJson(`/api/admin/nightly-checks/latest/?kind=${encodeURIComponent(kind)}`);
}

/** Start the AI review of the commits pushed since the last review (runs a few minutes on the server). */
export function startAiReview() {
  return postJson("/api/admin/code-review/ai/run/", {});
}

/** How that AI review is going: {state: idle|running|done|failed, result, error, available, why}. */
export function fetchAiReviewStatus() {
  return getJson("/api/admin/code-review/ai/status/");
}

/** Size the nightly AI review's open findings saved without an impact, now (each tab's manual run asks for
 * it; one sizing covers all three tabs). It runs in the background: follow it with fetchSizingStatus. */
export function startSizingFindings() {
  return postJson("/api/admin/code-findings/size/", {});
}

/** How that sizing is going: {state: idle|running|done|failed, result: {asked, estimated}, error}. */
export function fetchSizingStatus() {
  return getJson("/api/admin/code-findings/size/");
}

/** Scan the whole backend for code patterns that cost memory, on the live backend. */
export function runMemoryScan() {
  return postJson("/api/admin/memory-issues/scan/", {});
}

/** The Alerts tab: who gets the Live Activity emails, the database watcher and the latest emails sent. */
export function fetchAlerts() {
  return getJson("/api/admin/alerts/");
}

/** Add someone to the email lists, or change their settings: {id?, email, timezone, morning_hour, morning, urgent}. */
export function saveAlertRecipient(recipient) {
  return postJson("/api/admin/alerts/recipients/", recipient);
}

/** Take someone off the email lists. */
export function removeAlertRecipient(id) {
  return postJson(`/api/admin/alerts/recipients/${encodeURIComponent(id)}/delete/`, {});
}

/** Send one test email to this address (the server allows one a minute per address). */
export function sendTestAlert(email) {
  return postJson("/api/admin/alerts/test/", { email });
}
